// Emergency Panic Button Component
// Triggers emergency detachment when Alt key is pressed 4 times

import React, { useState, useEffect, useCallback } from 'react';
import './EmergencyPanicButton.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';

interface EmergencyState {
  isDetached: boolean;
  detachedAt: string | null;
  reason: string | null;
  backupCompleted: boolean;
}

const EmergencyPanicButton: React.FC = () => {
  const [altKeyCount, setAltKeyCount] = useState(0);
  const [emergencyState, setEmergencyState] = useState<EmergencyState | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [resetTimer, setResetTimer] = useState<NodeJS.Timeout | null>(null);

  // Check emergency status on component mount
  useEffect(() => {
    checkEmergencyStatus();
  }, []);

  const checkEmergencyStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/emergency/status`);
      const data = await response.json();
      if (data.success) {
        setEmergencyState(data.emergency_state);
      }
    } catch (error) {
      console.error('Failed to check emergency status:', error);
    }
  };

  const resetAltKeyCount = useCallback(() => {
    setAltKeyCount(0);
    if (resetTimer) {
      clearTimeout(resetTimer);
      setResetTimer(null);
    }
  }, [resetTimer]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Alt') {
      event.preventDefault();
      
      setAltKeyCount(prev => {
        const newCount = prev + 1;
        
        // Clear existing timer
        if (resetTimer) {
          clearTimeout(resetTimer);
        }
        
        // Set new timer to reset count after 3 seconds
        const timer = setTimeout(resetAltKeyCount, 3000);
        setResetTimer(timer);
        
        // If 4th Alt key press, show confirmation modal
        if (newCount >= 4) {
          setShowModal(true);
          resetAltKeyCount();
        }
        
        return newCount;
      });
    }
  }, [resetTimer, resetAltKeyCount]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (resetTimer) {
        clearTimeout(resetTimer);
      }
    };
  }, [handleKeyDown, resetTimer]);

  const triggerEmergencyDetachment = async (reason: string) => {
    setIsTriggering(true);
    try {
      const response = await fetch(`${API_BASE_URL}/emergency/panic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('🚨 Emergency detachment successful');
        await checkEmergencyStatus();
        alert('⚠️ EMERGENCY MODE ACTIVATED\n\nSystems database has been detached.\nApplication is now running in safe mode.');
      } else {
        console.error('Emergency detachment failed:', data.message);
        alert('❌ Emergency detachment failed: ' + data.message);
      }
    } catch (error) {
      console.error('Emergency detachment error:', error);
      alert('❌ Emergency detachment failed due to network error');
    } finally {
      setIsTriggering(false);
      setShowModal(false);
    }
  };

  const handleConfirmPanic = () => {
    const reason = prompt('🚨 EMERGENCY DETACHMENT\n\nThis will immediately disconnect the systems database and activate safe mode.\n\nPlease enter a reason for this action:');
    
    if (reason && reason.trim()) {
      triggerEmergencyDetachment(reason.trim());
    } else {
      setShowModal(false);
    }
  };

  const handleCancelPanic = () => {
    setShowModal(false);
  };

  // Emergency status indicator
  if (emergencyState?.isDetached) {
    return (
      <div className="emergency-indicator emergency-active">
        <div className="emergency-icon">🚨</div>
        <div className="emergency-text">
          <strong>EMERGENCY MODE</strong>
          <br />
          <small>Receivables Detached</small>
          <br />
          <small>{emergencyState.detachedAt && new Date(emergencyState.detachedAt).toLocaleString()}</small>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Alt key counter (for debugging) */}
      {altKeyCount > 0 && (
        <div className="alt-key-counter">
          Alt pressed: {altKeyCount}/4
        </div>
      )}

      {/* Emergency confirmation modal */}
      {showModal && (
        <div className="emergency-modal-overlay">
          <div className="emergency-modal">
            <div className="emergency-modal-header">
              <h2>🚨 EMERGENCY DETACHMENT</h2>
            </div>
            <div className="emergency-modal-body">
              <p>
                <strong>You are about to trigger emergency mode!</strong>
              </p>
              <p>
                This will immediately:
              </p>
              <ul>
                <li>Disconnect the systems database</li>
                <li>Create an emergency backup</li>
                <li>Switch to safe mode operation</li>
                <li>Log this security event</li>
              </ul>
              <p>
                <strong>Continue only if you suspect a security breach!</strong>
              </p>
            </div>
            <div className="emergency-modal-actions">
              <button 
                className="emergency-confirm-btn"
                onClick={handleConfirmPanic}
                disabled={isTriggering}
              >
                {isTriggering ? 'Triggering...' : '🚨 TRIGGER EMERGENCY'}
              </button>
              <button 
                className="emergency-cancel-btn"
                onClick={handleCancelPanic}
                disabled={isTriggering}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Normal mode indicator */}
      <div className="emergency-indicator emergency-normal" style={{ display: 'none' }}>
        <div className="emergency-icon">🛡️</div>
        <div className="emergency-text">
          <small>Emergency System Ready</small>
        </div>
      </div>
    </>
  );
};

export default EmergencyPanicButton;
